import { useEffect, useState } from "react";
import api, { formatIDR, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { UserCircle, ShieldCheck, QrCode, User, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyUser = { name: "", email: "", phone: "", password: "", role: "user" };

const roleMeta = {
  admin: { label: "Admin", cls: "bg-secondary/15 text-secondary", icon: ShieldCheck },
  scanner: { label: "Petugas", cls: "bg-primary/15 text-primary", icon: QrCode },
  user: { label: "Pembeli", cls: "bg-white/10 text-zinc-300", icon: User },
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Role diperbarui"); load(); }
    catch { toast.error("Gagal memperbarui role"); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setForm(emptyUser); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, phone: u.phone || "", password: "", role: u.role }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    if (!form.name || (!editId && (!form.email || !form.password))) { toast.error("Nama, email & password wajib diisi"); return; }
    setSaving(true);
    try {
      if (editId) {
        const payload = { name: form.name, phone: form.phone, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editId}`, payload);
      } else {
        await api.post("/admin/users", form);
      }
      toast.success(editId ? "Pengguna diperbarui" : "Pengguna ditambahkan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan"); }
    setSaving(false);
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/users/${deleteUser.id}`); toast.success("Pengguna dihapus"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menghapus"); }
    setDeleteUser(null);
  };

  const totalSpent = users.reduce((s, u) => s + (u.total_spent || 0), 0);

  return (
    <AdminLayout title="Pengguna Terdaftar">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-card border border-border/40 p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Total Pengguna</p>
          <p className="font-heading text-3xl font-black text-primary">{users.length}</p>
        </div>
        <div className="bg-card border border-border/40 p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Admin</p>
          <p className="font-heading text-3xl font-black text-secondary">{users.filter((u) => u.role === "admin").length}</p>
        </div>
        <div className="bg-card border border-border/40 p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Petugas</p>
          <p className="font-heading text-3xl font-black text-white">{users.filter((u) => u.role === "scanner").length}</p>
        </div>
        <div className="bg-card border border-border/40 p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Total Belanja</p>
          <p className="font-heading text-2xl font-black text-[#00FF66]">{formatIDR(totalSpent)}</p>
        </div>
      </div>

      <div className="flex justify-end mb-4">
        <Button data-testid="new-user-btn" onClick={openNew} className="bg-primary text-black font-bold hover:bg-cyan-300 rounded-none gap-2">
          <Plus size={18} weight="bold" /> Tambah Pengguna
        </Button>
      </div>

      <div className="bg-card border border-border/40 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-zinc-500">Memuat...</div>
        ) : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-border/40 text-xs uppercase tracking-wider">
                <th className="p-3 font-semibold">Nama</th>
                <th className="p-3 font-semibold">Email</th>
                <th className="p-3 font-semibold">No HP</th>
                <th className="p-3 font-semibold">Login</th>
                <th className="p-3 font-semibold">Order</th>
                <th className="p-3 font-semibold">Tiket</th>
                <th className="p-3 font-semibold">Belanja</th>
                <th className="p-3 font-semibold">Role</th>
                <th className="p-3 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const rm = roleMeta[u.role] || roleMeta.user;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border/20 hover:bg-white/5">
                    <td className="p-3 font-semibold text-white flex items-center gap-2">
                      <UserCircle size={20} weight="fill" className="text-primary shrink-0" /> {u.name}
                    </td>
                    <td className="p-3 text-zinc-400">{u.email}</td>
                    <td className="p-3 text-zinc-400">{u.phone || "-"}</td>
                    <td className="p-3 text-zinc-500 uppercase text-[10px] tracking-wider">{u.auth_provider || "email"}</td>
                    <td className="p-3 text-zinc-300">{u.orders_count}</td>
                    <td className="p-3 text-zinc-300">{u.tickets_count}</td>
                    <td className="p-3 text-primary font-semibold">{formatIDR(u.total_spent)}</td>
                    <td className="p-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                        <SelectTrigger data-testid={`role-select-${u.id}`} className={`w-32 h-8 rounded-none border-white/10 text-xs font-bold uppercase tracking-wider ${rm.cls}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10 rounded-none">
                          <SelectItem value="user">Pembeli</SelectItem>
                          <SelectItem value="scanner">Petugas</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} variant="outline" size="icon" className="h-8 w-8 border-white/20 rounded-none hover:bg-primary hover:text-black">
                          <PencilSimple size={15} />
                        </Button>
                        <Button data-testid={`delete-user-${u.id}`} onClick={() => setDeleteUser(u)} variant="outline" size="icon" className="h-8 w-8 border-white/20 rounded-none hover:bg-secondary hover:text-white hover:border-secondary">
                          <Trash size={15} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-white/10 rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Nama</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={set("name")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} onChange={set("email")} disabled={!!editId}
                className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary disabled:opacity-50" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">No HP</Label>
              <Input data-testid="user-phone-input" value={form.phone} onChange={set("phone")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">{editId ? "Password Baru (opsional)" : "Password"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={set("password")} className="mt-1 bg-zinc-900 border-zinc-800 rounded-none focus:ring-primary focus:border-primary" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="mt-1 bg-zinc-900 border-zinc-800 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-white/10 rounded-none">
                  <SelectItem value="user">Pembeli</SelectItem>
                  <SelectItem value="scanner">Petugas</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="save-user-btn" onClick={save} disabled={saving} className="w-full bg-primary text-black font-bold hover:bg-cyan-300 rounded-none h-11">
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteUser} onOpenChange={(o) => !o && setDeleteUser(null)}>
        <AlertDialogContent className="bg-card border-white/10 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus pengguna ini?</AlertDialogTitle>
            <AlertDialogDescription>{deleteUser?.name} ({deleteUser?.email}) akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-white/20">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user-btn" onClick={doDelete} className="bg-secondary text-white hover:bg-secondary/80 rounded-none">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
